/**
 * Demo v2 · Participant view (mobile-web).
 *
 * Public unauthenticated route mounted only inside the isolated Demo
 * v2 environment (``demo.veralok.com/demo-citizen/{consent_id}``).
 * A real person opens this view on their own phone during a Lane 1
 * or Lane 2 trial and decides on an incoming verification request
 * from the evaluator institution.
 *
 * This view never touches production citizen identity, KYC, consent,
 * or institutional authority. The decision is submitted through
 * ``POST /api/demo/citizen/decide/{consent_id}`` which is enforced
 * demo-only server-side.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, ShieldAlert, ShieldX, ArrowRight } from "lucide-react";
import VeraLokLogo from "@/marketing/VeraLokLogo";

/** Large unmistakable DEMO V2 header carried on every phone-side state. */
function DemoV2Header() {
  return (
    <div
      className="vlk-demo-citizen-banner"
      data-testid="demo-citizen-banner"
    >
      <VeraLokLogo size={22} />
      <span className="vlk-demo-citizen-banner-tag">DEMO&nbsp;V2</span>
      <span className="vlk-demo-citizen-banner-meta">
        Isolated Demo v2 interaction · Not production
      </span>
    </div>
  );
}

export default function DemoCitizen() {
  const { consent_id } = useParams();
  const [consent, setConsent] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/demo/citizen/consent/${consent_id}`)
      .then((r) => alive && setConsent(r.data))
      .catch((e) => alive && setError(
        e?.response?.data?.detail?.reason_code || "not_found"));
    return () => { alive = false; };
  }, [consent_id]);

  const submit = async (verdict) => {
    if (!consent) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/demo/citizen/decide/${consent_id}`,
        { decision: verdict, vlk_id: consent.vlk_id },
      );
      setDecision(data);
    } catch (e) {
      setError(e?.response?.data?.detail?.reason_code || "decide_failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vlk-demo-citizen" data-testid="demo-citizen-page">
      <div className="vlk-demo-citizen-card">
        <DemoV2Header />
        {error && !consent && (
          <ConsentNotFound reason={error} />
        )}
        {!consent && !error && (
          <div className="text-center text-sm text-white/50">Loading…</div>
        )}
        {consent && !decision && (
          <ConsentRequestView
            consent={consent} onDecide={submit} busy={busy} />
        )}
        {consent && decision && (
          <DecisionReceipt consent={consent} decision={decision} />
        )}
        <div
          className="mt-6 pt-4 border-t border-white/10 text-[11px] text-white/40 leading-relaxed"
          data-testid="demo-citizen-footer-note"
        >
          This is a Demo v2 participant view. Nothing you approve or
          deny here touches production VeraLok identity, KYC, consent
          or institutional authority. If VeraLok later onboards this
          institution, they enter the separate production
          institutional onboarding lifecycle.
        </div>
      </div>
    </div>
  );
}

function ConsentRequestView({ consent, onDecide, busy }) {
  return (
    <>
      <div className="text-xs uppercase tracking-widest text-white/50 mb-1">
        Verification request
      </div>
      <h1 className="text-xl font-semibold mb-3" data-testid="demo-citizen-title">
        {consent.institution_name || "A Demo v2 institution"} is asking
        to verify your identity.
      </h1>
      <dl className="text-sm space-y-2 mb-4">
        <div className="flex justify-between">
          <dt className="text-white/50">Purpose</dt>
          <dd className="text-white text-right max-w-[70%]"
              data-testid="demo-citizen-purpose">
            {consent.purpose || "—"}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-white/50">Institution sector</dt>
          <dd className="text-white">
            {consent.institution_industry || "—"}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-white/50">Participant (you)</dt>
          <dd className="text-white">
            {consent.citizen_display_name || consent.vlk_id}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-white/50">Consent ID</dt>
          <dd className="text-white/70 font-mono text-xs">
            {consent.consent_id}
          </dd>
        </div>
      </dl>
      <div className="text-xs uppercase tracking-widest text-white/50 mb-1">
        Claims they can see if you approve
      </div>
      <div className="flex flex-wrap gap-1 mb-4">
        {(consent.scope || []).map((c) => (
          <span
            key={c}
            className="text-[11px] font-mono px-2 py-0.5 rounded-full
                       border border-white/15 text-white/80"
            data-testid={`demo-citizen-claim-${c}`}
          >
            {c}
          </span>
        ))}
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => onDecide("deny")}
          disabled={busy}
          className="flex-1 h-11 rounded-lg border border-white/15 text-white/80
                     hover:bg-white/5 font-semibold text-sm disabled:opacity-50"
          data-testid="demo-citizen-deny"
        >
          Deny
        </button>
        <button
          onClick={() => onDecide("approve")}
          disabled={busy}
          className="flex-1 h-11 rounded-lg bg-[var(--vlk-accent)] hover:brightness-110
                     text-[#001525] font-bold text-sm disabled:opacity-50"
          data-testid="demo-citizen-approve"
        >
          Approve
        </button>
      </div>
      {busy && (
        <div className="text-xs text-white/50 text-center mt-3">
          Recording decision…
        </div>
      )}
    </>
  );
}

function DecisionReceipt({ consent, decision }) {
  const isGrant = decision.state === "GRANTED";
  const Icon = isGrant ? CheckCircle2 : ShieldX;
  return (
    <div className="text-center" data-testid="demo-citizen-decision-receipt">
      <div
        className="vlk-demo-citizen-state-tag"
        data-testid="demo-citizen-state-tag"
      >
        DEMO&nbsp;V2 · Decision recorded
      </div>
      <Icon className={`w-12 h-12 mx-auto mb-2 ${
        isGrant ? "text-emerald-400" : "text-rose-400"}`} />
      <h2 className="text-lg font-semibold mb-1">
        {isGrant
          ? "You approved this Demo v2 verification"
          : "You denied this Demo v2 verification"}
      </h2>
      <p className="text-sm text-white/60 mb-4">
        {consent.institution_name} will see this outcome in their
        Verification Requests page inside the Demo v2 Institution
        Portal.
      </p>
      <div className="text-[11px] font-mono text-white/40">
        {consent.consent_id} → {decision.state}
      </div>
      <Link
        to="/institution"
        className="mt-4 inline-flex items-center gap-1 text-xs text-[var(--vlk-accent)]
                   hover:brightness-110"
      >
        Back to institution portal <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

function ConsentNotFound({ reason }) {
  const isExpired = reason === "not_found" || reason === "consent_not_found";
  return (
    <div className="text-center" data-testid="demo-citizen-not-found">
      <div
        className="vlk-demo-citizen-state-tag"
        data-testid="demo-citizen-state-tag"
      >
        DEMO&nbsp;V2 · {isExpired ? "Expired or unavailable" : "Blocked"}
      </div>
      <ShieldAlert className="w-12 h-12 mx-auto mb-2 text-rose-400" />
      <div className="text-xs uppercase tracking-widest text-white/50 mb-1">
        {reason.replace(/_/g, " ")}
      </div>
      <h2 className="text-lg font-semibold mb-1">
        This Demo v2 request cannot be opened.
      </h2>
      <p className="text-sm text-white/60">
        It may have expired or been rescinded. Ask the evaluator to
        submit a fresh Request Verification from the Demo v2
        Institution Portal.
      </p>
    </div>
  );
}
